'use client';

import React, { useState, useEffect } from 'react';
import { Navbar, Footer } from '../../components/Layout';
import { Truck, ShieldCheck, Clock, HelpCircle, MapPin } from 'lucide-react';

export default function ShippingPolicyPage() {
  const [shippingPolicyContent, setShippingPolicyContent] = useState<string>('');

  useEffect(() => {
    const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
    fetch(`${authApi}/api/settings`)
      .then(res => res.json())
      .then(settings => {
        if (settings.shipping_policy_content) {
          setShippingPolicyContent(settings.shipping_policy_content);
        }
      })
      .catch(err => console.error('Error fetching shipping policy settings:', err));
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9F5] font-body text-text-primary flex flex-col justify-between">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 w-full flex-grow text-left">
        {/* Page Header */}
        <div className="mb-10 border-b border-border/60 pb-6">
          <h1 className="text-4xl font-display font-black text-deep-green">Shipping & Delivery Policy</h1>
          <p className="text-text-muted text-sm font-medium mt-2">
            Learn about our shipping procedures, delivery timelines, and how we preserve your fresh organic harvests during transit.
          </p>
        </div>

        <div className="space-y-8">
          {/* Quick Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-border/40 shadow-sm flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-pale-green/30 rounded-full flex items-center justify-center text-mid-green mb-4">
                <Clock className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-deep-green text-base mb-1">Standard Delivery</h3>
              <p className="text-xs text-text-muted">1 to 2 working days across Tamil Nadu & neighbouring regions.</p>
            </div>
            
            <div className="bg-white p-6 rounded-2xl border border-border/40 shadow-sm flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-pale-green/30 rounded-full flex items-center justify-center text-mid-green mb-4">
                <Truck className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-deep-green text-base mb-1">Shipping Fee</h3>
              <p className="text-xs text-text-muted">Calculated dynamically based on your delivery coordinates/pincode.</p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-border/40 shadow-sm flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-pale-green/30 rounded-full flex items-center justify-center text-mid-green mb-4">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-deep-green text-base mb-1">Temperature Managed</h3>
              <p className="text-xs text-text-muted">Insulated eco-friendly boxes for fresh meats, vegetables, and milk products.</p>
            </div>
          </div>

          {/* Policy Sections */}
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-border/40 shadow-sm space-y-6">
            {shippingPolicyContent ? (
              <div className="text-sm text-text-muted leading-relaxed whitespace-pre-line">
                {shippingPolicyContent}
              </div>
            ) : (
              <>
                <section className="space-y-3">
                  <h2 className="text-xl font-bold text-deep-green flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-mid-green" /> Delivery Coverage & Partners
                  </h2>
                  <p className="text-sm text-text-muted leading-relaxed">
                    We deliver to all major cities and towns in Tamil Nadu and select regions in neighbouring states. We partner with reliable express logistics networks to ensure that your packages arrive on time. For fresh perishables, we operate dedicated local temperature-controlled fleets.
                  </p>
                </section>

                <section className="space-y-3">
                  <h2 className="text-xl font-bold text-deep-green flex items-center gap-2">
                    <Clock className="w-5 h-5 text-mid-green" /> Processing & Dispatch Timings
                  </h2>
                  <p className="text-sm text-text-muted leading-relaxed">
                    Orders placed before 12:00 PM are dispatched on the same day from our organic farming hubs in Erode. Orders placed after 12:00 PM will be processed the following business day. Perishables such as fresh farm meats are harvested and packed in the early morning to guarantee absolute freshness upon delivery.
                  </p>
                </section>

                <section className="space-y-3">
                  <h2 className="text-xl font-bold text-deep-green flex items-center gap-2">
                    <Truck className="w-5 h-5 text-mid-green" /> Coordinate-Pinned Address Delivery
                  </h2>
                  <p className="text-sm text-text-muted leading-relaxed">
                    To prevent order routing issues and ensure that our dispatch riders can locate your home efficiently, we leverage coordinate-pinned map selection during checkout. Please verify your pinned coordinates to experience seamless delivery.
                  </p>
                </section>

                <section className="space-y-3">
                  <h2 className="text-xl font-bold text-deep-green flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-mid-green" /> Delay Notifications
                  </h2>
                  <p className="text-sm text-text-muted leading-relaxed">
                    While we strive to meet all delivery timelines, occasionally unexpected traffic, harvest constraints, or extreme weather conditions can cause delays. In such rare instances, you will receive real-time SMS/Email notification updates, and you can monitor order tracking milestones directly from your <span className="font-bold text-deep-green">My Orders</span> tab.
                  </p>
                </section>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
